import { useState } from "react";
import { Copy, Check, Mail, MessageCircle } from "lucide-react";
import { CONTACT } from "../../config/site";

export default function EnquiryForm({ fields, submitLabel = "Submit Enquiry", testId = "enquiry-form", intro }) {
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const setValue = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const validate = () => {
    const errs = {};
    fields.forEach((f) => {
      const v = (values[f.name] || "").trim();
      if (f.required && !v) errs[f.name] = `${f.label} is required.`;
      if (f.type === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) errs[f.name] = "Enter a valid email address.";
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const composedText = fields
    .map((f) => `${f.label}: ${(values[f.name] || "").trim() || "—"}`)
    .join("\n");

  const onSubmit = (e) => {
    e.preventDefault();
    if (validate()) setSubmitted(true);
  };

  const copyEnquiry = async () => {
    try {
      await navigator.clipboard.writeText(composedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  if (submitted) {
    const mailHref = `mailto:${CONTACT.email}?subject=${encodeURIComponent("Enquiry — The Vantage Institute")}&body=${encodeURIComponent(composedText)}`;
    const waHref = `${CONTACT.whatsapp}?text=${encodeURIComponent(composedText)}`;
    return (
      <div className="card border-gold" data-testid={`${testId}-submitted`}>
        <h3 className="font-sub text-lg font-semibold text-navy">Your enquiry is ready to send</h3>
        <span className="gold-rule mt-3" aria-hidden="true" />
        <p className="mt-4 text-sm leading-relaxed text-slate">
          Choose how to send it — email or WhatsApp will open with your enquiry pre-filled and addressed to the
          Institute. The team will respond directly.
        </p>
        <pre className="mt-5 max-h-56 overflow-y-auto whitespace-pre-wrap border border-platinum bg-paper p-4 font-body text-sm text-navy" data-testid={`${testId}-composed`}>
          {composedText}
        </pre>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <a href={mailHref} className="btn-gold" data-testid={`${testId}-send-email`}>
            <Mail size={16} aria-hidden="true" />
            Send via Email
          </a>
          <a href={waHref} target="_blank" rel="noopener noreferrer" className="btn-navy" data-testid={`${testId}-send-whatsapp`}>
            <MessageCircle size={16} aria-hidden="true" />
            Send via WhatsApp
          </a>
          <button type="button" onClick={copyEnquiry} className="btn-outline-navy" data-testid={`${testId}-copy`}>
            {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
            {copied ? "Copied" : "Copy Enquiry"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => { setSubmitted(false); setValues({}); }}
          className="mt-4 font-sub text-xs font-semibold uppercase tracking-[0.12em] text-slate underline underline-offset-4 hover:text-gold-deep"
          data-testid={`${testId}-reset`}
        >
          Start a new enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid={testId}>
      {intro && <p className="text-sm leading-relaxed text-slate">{intro}</p>}
      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
            <label htmlFor={`${testId}-${f.name}`} className="input-label">
              {f.label}
              {f.required && <span aria-hidden="true" className="text-gold-deep"> *</span>}
            </label>
            {f.type === "textarea" ? (
              <textarea
                id={`${testId}-${f.name}`}
                rows={4}
                className="input-field"
                value={values[f.name] || ""}
                onChange={(e) => setValue(f.name, e.target.value)}
                aria-required={f.required || undefined}
                aria-invalid={!!errors[f.name]}
                aria-describedby={errors[f.name] ? `${testId}-${f.name}-error` : undefined}
                data-testid={`${testId}-field-${f.name}`}
              />
            ) : f.type === "select" ? (
              <select
                id={`${testId}-${f.name}`}
                className="input-field"
                value={values[f.name] || ""}
                onChange={(e) => setValue(f.name, e.target.value)}
                aria-required={f.required || undefined}
                aria-invalid={!!errors[f.name]}
                aria-describedby={errors[f.name] ? `${testId}-${f.name}-error` : undefined}
                data-testid={`${testId}-field-${f.name}`}
              >
                <option value="">Select…</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                id={`${testId}-${f.name}`}
                type={f.type || "text"}
                className="input-field"
                value={values[f.name] || ""}
                onChange={(e) => setValue(f.name, e.target.value)}
                aria-required={f.required || undefined}
                aria-invalid={!!errors[f.name]}
                aria-describedby={errors[f.name] ? `${testId}-${f.name}-error` : undefined}
                data-testid={`${testId}-field-${f.name}`}
              />
            )}
            {errors[f.name] && (
              <p id={`${testId}-${f.name}-error`} role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`${testId}-error-${f.name}`}>
                {errors[f.name]}
              </p>
            )}
          </div>
        ))}
      </div>
      <button type="submit" className="btn-gold w-full sm:w-auto" data-testid={`${testId}-submit`}>
        {submitLabel}
      </button>
      <p className="text-xs leading-relaxed text-slate">
        On submit, your enquiry opens pre-filled in your email or WhatsApp, addressed to the Institute — nothing is stored on this website.
      </p>
    </form>
  );
}
